/**
 * 通用发布-订阅类
 *
 * 提供类型安全的事件发布订阅功能，支持：
 * - on: 普通订阅
 * - once: 一次性订阅
 * - replay: 重放最后一次事件的订阅
 * - emit: 异步事件发布
 * - off: 取消订阅
 *
 * @template T - 事件类型映射对象
 *
 * @example
 * ```typescript
 * interface Events {
 *   message: string;
 *   click: MouseEvent;
 * }
 *
 * const events = new Subscribe<Events>();
 * events.on('message').subscribe((msg) => console.log(msg));
 * events.emit('message', 'Hello');
 * ```
 */
export class Subscribe<T> {
  private subscriptions: Map<keyof T, Set<(data: T[keyof T]) => void>> =
    new Map();

  /** 存储最近的事件数据，用于 replay 功能 */
  private lastEmitted: Map<keyof T, T[keyof T]> = new Map();

  private createSubscription<K extends keyof T>(
    key: K,
    immediateCallback?: (callback: (data: T[K]) => void) => void,
  ): {
    subscribe: (callback: (data: T[K]) => void) => {
      unsubscribe: () => void;
    };
    unsubscribe: () => void;
  } {
    if (!this.subscriptions.has(key)) {
      this.subscriptions.set(key, new Set());
    }

    const subscribe = (
      callback: (data: T[K]) => void,
    ): { unsubscribe: () => void } => {
      const callbacks = this.subscriptions.get(key) as Set<
        (data: T[K]) => void
      >;

      if (immediateCallback) {
        immediateCallback(callback);
      } else {
        callbacks.add(callback);
      }

      return { unsubscribe: () => this.off(key, callback) };
    };

    return { subscribe, unsubscribe: (): void => this.off(key) };
  }

  /**
   * 订阅事件
   *
   * @param key - 事件键
   * @returns 订阅对象，包含 subscribe 和 unsubscribe 方法
   *
   * @example
   * ```typescript
   * const subscription = events.on('message').subscribe((data) => {
   *   console.log('收到消息:', data);
   * });
   *
   * // 取消订阅
   * subscription.unsubscribe();
   * ```
   */
  on<K extends keyof T>(
    key: K,
  ): {
    subscribe: (callback: (data: T[K]) => void) => {
      unsubscribe: () => void;
    };
    unsubscribe: () => void;
  } {
    return this.createSubscription(key);
  }

  /**
   * 只执行一次的订阅
   *
   * 回调函数执行后会自动取消订阅。
   *
   * @param key - 事件键
   * @returns 订阅对象
   *
   * @example
   * ```typescript
   * events.once('page_load').subscribe((data) => {
   *   console.log('页面加载完成:', data);
   *   // 此回调只会执行一次
   * });
   * ```
   */
  once<K extends keyof T>(
    key: K,
  ): { subscribe: (callback: (data: T[K]) => void) => void } {
    return this.createSubscription(key, (callback) => {
      const callbacks = this.subscriptions.get(key) as Set<
        (data: T[K]) => void
      >;
      let called = false;
      const once = (data: T[K]): void => {
        if (called) {
          return;
        }
        called = true;
        callback(data);
        this.off(key, once);
      };
      callbacks.add(once);
    });
  }

  /**
   * 发布事件
   *
   * 使用 queueMicrotask 异步调用所有订阅的回调函数。
   *
   * @param key - 事件键
   * @param data - 事件数据
   *
   * @example
   * ```typescript
   * events.emit('message', { type: 'info', text: 'Hello' });
   * ```
   */
  emit<K extends keyof T>(key: K, data: T[K]): void {
    this.lastEmitted.set(key, data); // 记录最近一次的事件数据
    const callbacks = this.subscriptions.get(key) as
      | Set<(data: T[K]) => void>
      | undefined;
    if (callbacks) {
      callbacks.forEach((callback) => {
        if (typeof queueMicrotask === "function") {
          queueMicrotask(() => {
            callback(data);
          });
        } else {
          setTimeout(() => callback(data), 0);
        }
      });
    }
  }

  /**
   * 取消订阅
   *
   * @param key - 可选，事件键。如果不提供，则清除所有订阅
   * @param callback - 可选，特定的回调函数。如果不提供，则清除该事件的所有订阅
   *
   * @example
   * ```typescript
   * // 取消特定回调
   * events.off('message', myCallback);
   *
   * // 取消事件的所有订阅
   * events.off('message');
   *
   * // 清除所有订阅
   * events.off();
   * ```
   */
  off<K extends keyof T>(key?: K, callback?: (data: T[K]) => void): void {
    if (key == undefined) {
      this.subscriptions.clear(); // 移除所有订阅
      this.lastEmitted.clear(); // 移除所有订阅
      return;
    }

    if (!callback) {
      this.subscriptions.delete(key); // 仅移除该 key
      this.lastEmitted.delete(key); // 仅移除该 key
      return;
    }

    const callbacks = this.subscriptions.get(key);
    if (!callbacks) {
      return;
    }

    (callbacks as Set<(data: T[K]) => void>).delete(callback);
    if (callbacks.size === 0) {
      this.subscriptions.delete(key);
    }
  }

  /**
   * 重放订阅
   *
   * 订阅事件，如果该事件之前已经发布过，则立即执行一次回调（使用最后一次的数据）。
   *
   * @param key - 事件键
   * @returns 订阅对象
   *
   * @example
   * ```typescript
   * // 即使事件已经发布过，也会立即收到最后一次的数据
   * events.replay('message').subscribe((data) => {
   *   console.log('收到或重放消息:', data);
   * });
   * ```
   */
  replay<K extends keyof T>(
    key: K,
  ): {
    subscribe: (callback: (data: T[K]) => void) => {
      unsubscribe: () => void;
    };
    unsubscribe: () => void;
  } {
    return this.createSubscription(key, (callback) => {
      const callbacks = this.subscriptions.get(key) as Set<
        (data: T[K]) => void
      >;
      callbacks.add(callback);
      if (this.lastEmitted.has(key)) {
        const lastData = this.lastEmitted.get(key) as T[K];
        if (typeof queueMicrotask === "function") {
          queueMicrotask(() => callback(lastData));
        } else {
          setTimeout(() => callback(lastData), 0);
        }
      }
    });
  }
}
